// Segment results: what each segment's pre-leads came to, with the agent's suggestion (scale, keep, pause). Founder
// decision 2026-10-27: the agent only suggests; Apply or Dismiss is the User's click, and only Apply changes the
// saved night plan the lead agent and the nightly run search with. Each click is written to the outreach log.
import { useQuery } from "@tanstack/react-query";
import {
  useCanAccess,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { CrmDataProvider } from "../../providers/types";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../../root/ConfigurationContext";
import {
  applySuggestion,
  isPaused,
  labelSegment,
  MAX_WEIGHT,
  weightOf,
  type Applicable,
  type SegmentLabel,
  type SegmentResult,
} from "../../../../../supabase/functions/_shared/segmentResults.ts";

const TONE: Record<SegmentLabel, "default" | "secondary" | "outline"> = {
  scaling: "default",
  working: "secondary",
  paused: "outline",
  too_early: "outline",
};
const COUNTS = [
  "found",
  "kept",
  "removed",
  "sent",
  "replied",
  "interested",
  "converted",
] as const;

export const SegmentResults = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const config = useConfigurationContext();
  const setConfig = useConfigurationUpdater();
  // only an admin saves the configuration; sales see the suggestions
  const { canAccess: canApply } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });
  const [saving, setSaving] = useState<string | null>(null);
  const { data: results = [] } = useQuery({
    queryKey: ["pre_lead_segment_results"],
    queryFn: async () =>
      (
        await dataProvider.getList("pre_lead_segment_results", {
          pagination: { page: 1, perPage: 100 },
          sort: { field: "found", order: "DESC" },
          filter: {},
        })
      ).data as unknown as SegmentResult[],
  });
  if (!results.length)
    return (
      <p className="py-8 text-center text-muted-foreground">
        {translate("resources.pre_leads.segment_results.empty")}
      </p>
    );
  const plan = config.lead_night_plan;
  const t = (key: string, options?: object) =>
    translate(`resources.pre_leads.segment_results.${key}`, options);

  const decide = async (
    name: string,
    label: Applicable,
    reason: string,
    apply: boolean,
  ) => {
    setSaving(name);
    try {
      const next = apply
        ? applySuggestion(plan, name, label)
        : {
            ...(plan ?? { offer: "", segments: [] }),
            dismissed: { ...plan?.dismissed, [name]: label },
          };
      setConfig(
        await dataProvider.updateConfiguration({
          ...config,
          lead_night_plan: next,
        }),
      );
      await dataProvider.create("outreach_log", {
        data: {
          step: apply
            ? "segment_suggestion_applied"
            : "segment_suggestion_dismissed",
          detail: { segment: name, label, reason, sales_id: identity?.id },
        },
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    } finally {
      setSaving(null);
    }
  };

  return (
    <section
      aria-label={t("title")}
      className="mb-3 rounded-md border p-3 flex flex-col gap-2"
    >
      <div>
        <h3 className="font-medium">{t("title")}</h3>
        <p className="text-xs text-muted-foreground">{t("help")}</p>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("segment")}</TableHead>
              {COUNTS.map((c) => (
                <TableHead key={c} className="text-right">
                  {t(`counts.${c}`)}
                </TableHead>
              ))}
              <TableHead>{t("suggestion")}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.map((r) => {
              const name = r.segment_name;
              const { label, reason } = labelSegment(r);
              const paused = isPaused(plan, name);
              const weight = weightOf(plan, name);
              const actionable =
                (label === "scaling" && weight < MAX_WEIGHT) ||
                (label === "paused" && !paused);
              const dismissed = plan?.dismissed?.[name] === label;
              return (
                <TableRow key={name}>
                  <TableCell className="font-medium">
                    {name}
                    {paused && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t("is_paused")}
                      </span>
                    )}
                    {weight > 1 && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {t("weight", { weight })}
                      </span>
                    )}
                  </TableCell>
                  {COUNTS.map((c) => (
                    <TableCell key={c} className="text-right tabular-nums">
                      {Number(r[c])}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <Badge variant={TONE[label]}>
                        {t(`labels.${label}`)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {reason}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {canApply && actionable && !dismissed && (
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button"
                          size="sm"
                          aria-label={t("apply_segment", { segment: name })}
                          disabled={saving !== null}
                          onClick={() =>
                            decide(name, label as Applicable, reason, true)
                          }
                        >
                          {t("apply")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          aria-label={t("dismiss_segment", { segment: name })}
                          disabled={saving !== null}
                          onClick={() =>
                            decide(name, label as Applicable, reason, false)
                          }
                        >
                          {t("dismiss")}
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
};
